import { X, DollarSign, Clock } from 'lucide-react';
import type { User } from '@/types';
import { useAuth } from '@/hooks/useAuth';
import { getUserColor, getRoleBadgeColor, getDisplayRole, getInitials } from '@/utils/calendarHelpers';
import { DAY_LABELS } from '@/utils/constants';
import { EarningsSummary } from '@/components/dashboard/EarningsSummary';
import { FamilyPaymentSection } from '@/components/family/PersonDetail';

interface ProfileModalProps {
  user: User;
  onClose: () => void;
}

export function ProfileModal({ user, onClose }: ProfileModalProps) {
  const { user: currentUser } = useAuth();
  const isGuardian = currentUser?.role === 'parent';

  return (
    <div data-testid="container-person-detail" className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
      <div className="bg-slate-800 rounded-xl border border-slate-700 w-full max-w-md shadow-xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-slate-700">
          <h3 className="text-lg font-semibold text-white">Profile</h3>
          <button data-testid="clickable-profile-modal-close" onClick={onClose} className="p-1 hover:bg-slate-700 rounded-lg transition-colors">
            <X size={20} />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          {/* Avatar and name */}
          <div className="flex flex-col items-center mb-6">
            {user.emoji ? (
              <div
                className="w-20 h-20 rounded-full flex items-center justify-center text-4xl mb-3"
                style={{
                  backgroundColor: `${getUserColor(user, '#3b82f6')}30`,
                }}
              >
                {user.emoji}
              </div>
            ) : (
              <div
                className="w-20 h-20 rounded-full flex items-center justify-center text-white text-2xl font-semibold mb-3"
                style={{
                  backgroundColor: getUserColor(user, '#3b82f6'),
                }}
              >
                {getInitials(user)}
              </div>
            )}
            <h2 data-testid="container-person-detail-name" className="text-xl font-semibold text-white">
              {user.first_name} {user.last_name}
            </h2>
            <span data-testid="container-person-detail-role" className={`inline-block text-sm px-3 py-1 rounded mt-2 ${getRoleBadgeColor(user.role)}`}>
              {user.role === 'professional' && user.other_role_label ? user.other_role_label : getDisplayRole(user.role)}
            </span>
          </div>

          {/* Caretaker-specific info */}
          {user.role === 'caretaker' && (
            <div className="space-y-4">
              {/* Hourly wage and max hours */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {user.hourly_wage && (
                  <div className="bg-slate-700/50 rounded-lg p-3">
                    <div className="flex items-center gap-2 text-gray-400 text-sm mb-1">
                      <DollarSign size={14} />
                      Hourly Rate
                    </div>
                    <div className="text-white font-medium">${user.hourly_wage}/hr</div>
                  </div>
                )}
                {user.max_hours_per_month && (
                  <div className="bg-slate-700/50 rounded-lg p-3">
                    <div className="flex items-center gap-2 text-gray-400 text-sm mb-1">
                      <Clock size={14} />
                      Max Hours/Month
                    </div>
                    <div className="text-white font-medium">{user.max_hours_per_month} hrs</div>
                  </div>
                )}
              </div>

              {/* Preferred days off */}
              {user.preferred_days_off && user.preferred_days_off.length > 0 && (
                <div className="bg-slate-700/50 rounded-lg p-3">
                  <div className="text-gray-400 text-sm mb-2">Prefers Off</div>
                  <div className="flex flex-wrap gap-2">
                    {user.preferred_days_off.map((day: string) => (
                      <span key={day} className="px-2 py-1 bg-yellow-500/20 text-yellow-400 text-sm rounded">
                        {DAY_LABELS[day] || day}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Unavailable days */}
              {user.unavailable_days && user.unavailable_days.length > 0 && (
                <div className="bg-slate-700/50 rounded-lg p-3">
                  <div className="text-gray-400 text-sm mb-2">Cannot Work</div>
                  <div className="flex flex-wrap gap-2">
                    {user.unavailable_days.map((day: string) => (
                      <span key={day} className="px-2 py-1 bg-red-500/20 text-red-400 text-sm rounded">
                        {DAY_LABELS[day] || day}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Earnings section for caretaker and family roles */}
          {(user.role === 'caretaker' || user.role === 'family') && (
            <div className="mt-4 pt-4 border-t border-slate-700">
              <EarningsSummary userId={user.id} userRole={user.role} hourlyWage={user.hourly_wage ?? undefined} viewingOther compact />
            </div>
          )}

          {/* Payment tracking for family members (guardian only) */}
          {isGuardian && user.role === 'family' && (
            <div className="mt-4 pt-4 border-t border-slate-700">
              <FamilyPaymentSection userId={user.id} firstName={user.first_name} />
            </div>
          )}

          {/* Email (if parent or admin viewing) */}
          {user.email && (
            <div className="mt-4 pt-4 border-t border-slate-700">
              <div className="text-gray-400 text-sm mb-1">Email</div>
              <div className="text-white">{user.email}</div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-slate-700">
          <button
            onClick={onClose}
            data-testid="clickable-profile-modal-close-footer"
            className="w-full py-2 bg-slate-700 hover:bg-slate-600 text-white font-medium rounded-lg transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}
